import {useEffect, useRef} from 'react';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {StepId} from '../../../types/checkoutApp.types';
import {SPECS} from '../../../common/constants';
import {useControllerProps, UseControllerPropsResult} from '../Widget/ControllerContext';
import {PlaceOrderButtonDataHooks} from '../PlaceOrderButton/PlaceOrderButton';
import {FormDetailsButtonsDataHook} from '../CheckoutSteps/StepsManager/Components/DetailsFormButtons/DetailsFormButtons';
import {AdditionalDetailsStepOpenDataHook} from '../CheckoutSteps/Steps/AdditionalDetailsStep/Components/AdditionalDetailsStepOpen';

interface ModelContextToolParams {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: Record<string, string>;
  execute: (params: Record<string, unknown>) => {content: {type: string; text: string}[]; isError?: boolean};
}

export interface ModelContext {
  registerTool: (params: ModelContextToolParams) => void;
}

declare global {
  interface Navigator {
    modelContext?: ModelContext;
  }
}

type ControllerProps = Pick<UseControllerPropsResult, 'checkoutStore' | 'stepsManagerStore'>;

const emptyInputSchema = {
  type: 'object' as const,
  properties: {},
};

const clickButtonByDataHook = (dataHook: string): boolean => {
  const button = document.querySelector<HTMLButtonElement>(`[data-hook="${dataHook}"]`);
  if (button) {
    button.click();
    return true;
  }
  return false;
};

const continueStepHandlers: Record<string, (props: ControllerProps) => {text: string; isError?: boolean}> = {
  [StepId.memberLogin]: () => ({
    text: 'Cannot auto-advance the login step. The user must log in or sign up manually.',
    isError: true,
  }),
  [StepId.customerDetails]: () => {
    const clicked = clickButtonByDataHook(FormDetailsButtonsDataHook.continueButton);
    return clicked
      ? {text: 'Clicked continue on customer details step'}
      : {text: 'Customer details continue button not found on page', isError: true};
  },
  [StepId.deliveryMethod]: (props) => {
    void props.checkoutStore?.onDeliveryMethodContinue?.();
    return {text: 'Advanced from delivery method step'};
  },
  [StepId.additionalDetails]: () => {
    const clicked = clickButtonByDataHook(AdditionalDetailsStepOpenDataHook.continueButton);
    return clicked
      ? {text: 'Clicked continue on additional details step'}
      : {text: 'Additional details continue button not found on page', isError: true};
  },
};

export const registerTools = (modelContext: ModelContext, propsRef: React.MutableRefObject<ControllerProps>) => {
  modelContext.registerTool({
    name: 'refreshCheckout',
    description: 'Refresh the checkout data from the server to reflect the latest state',
    inputSchema: emptyInputSchema,
    annotations: {readOnlyHint: 'true'},
    execute: () => {
      void propsRef.current.checkoutStore?.onRefreshCheckout?.();
      return {content: [{type: 'text', text: 'Checkout data refresh triggered'}]};
    },
  });

  modelContext.registerTool({
    name: 'continueToNextStep',
    description:
      'Advance to the next checkout step. On the delivery method step this also sets a default method if needed. On other steps it clicks the continue button which triggers validation and submission.',
    inputSchema: emptyInputSchema,
    execute: () => {
      const {stepsManagerStore} = propsRef.current;
      const currentStepId = stepsManagerStore?.activeStep?.stepId;

      if (!currentStepId) {
        return {content: [{type: 'text', text: 'No active step found'}], isError: true};
      }

      const handler = continueStepHandlers[currentStepId];
      if (handler) {
        const result = handler(propsRef.current);
        return {content: [{type: 'text', text: result.text}], ...(result.isError ? {isError: true} : {})};
      }

      return {
        content: [{type: 'text', text: `Already on the last step (${currentStepId}), cannot advance further.`}],
      };
    },
  });

  modelContext.registerTool({
    name: 'getCheckoutSteps',
    description: 'Get the list of checkout steps and which step is currently active',
    inputSchema: emptyInputSchema,
    annotations: {readOnlyHint: 'true'},
    execute: () => {
      const {stepsManagerStore} = propsRef.current;
      const steps = stepsManagerStore?.stepsList?.map((step, index) => ({
        index,
        id: step.id,
        state: step.state,
      }));
      return {content: [{type: 'text', text: JSON.stringify(steps ?? [])}]};
    },
  });

  modelContext.registerTool({
    name: 'getCheckoutId',
    description:
      'Get the current checkout ID. Use this when calling checkout backend APIs that require the checkout ID.',
    inputSchema: emptyInputSchema,
    annotations: {readOnlyHint: 'true'},
    execute: () => {
      const checkoutId = propsRef.current.checkoutStore?.cart?.id;
      return {content: [{type: 'text', text: checkoutId ?? 'Checkout ID not available'}]};
    },
  });

  const getPlaceOrderBlockers = (): string[] => {
    const {checkoutStore, stepsManagerStore} = propsRef.current;
    const isOnLastStep = stepsManagerStore?.activeStep?.stepIndex === stepsManagerStore?.stepsList?.length - 1;

    const reasons: string[] = [];
    if (!isOnLastStep) {
      reasons.push(`Not on the payment step yet (current step: ${stepsManagerStore?.activeStep?.stepId ?? 'unknown'})`);
    }
    if (checkoutStore?.isPlaceOrderButtonDisabled) {
      reasons.push(
        'Place order button is disabled (possible causes: error violations, cart updating, or invalid delivery method)'
      );
    }
    return reasons;
  };

  modelContext.registerTool({
    name: 'canPlaceOrder',
    description:
      'Check whether the order can be placed right now. Returns whether the place order button is enabled and, if not, a hint about why.',
    inputSchema: emptyInputSchema,
    annotations: {readOnlyHint: 'true'},
    execute: () => {
      const reasons = getPlaceOrderBlockers();
      return {
        content: [
          {
            type: 'text',
            text: JSON.stringify({canPlaceOrder: reasons.length === 0, ...(reasons.length ? {reasons} : {})}),
          },
        ],
      };
    },
  });

  modelContext.registerTool({
    name: 'placeOrder',
    description:
      'Place the order. Assumes all checkout data (contact, shipping, billing, payment) has already been set. This triggers the server-side place order flow and navigates to the thank-you page on success.',
    inputSchema: emptyInputSchema,
    execute: () => {
      const reasons = getPlaceOrderBlockers();
      if (reasons.length) {
        return {content: [{type: 'text', text: `Cannot place order: ${reasons.join('; ')}`}], isError: true};
      }

      const clicked = clickButtonByDataHook(PlaceOrderButtonDataHooks.button);
      if (!clicked) {
        return {content: [{type: 'text', text: 'Place order button not found on page'}], isError: true};
      }
      return {content: [{type: 'text', text: 'Place order triggered'}]};
    },
  });
};

/* istanbul ignore next */
export const useRegisterWebMcpTools = () => {
  const {checkoutStore, stepsManagerStore} = useControllerProps();
  const {experiments} = useExperiments();
  const shouldRegister = experiments.enabled(SPECS.RegisterWebMcpTools);

  const propsRef = useRef<ControllerProps>({checkoutStore, stepsManagerStore});
  propsRef.current = {checkoutStore, stepsManagerStore};

  useEffect(() => {
    if (!shouldRegister) {
      return;
    }
    const modelContext = window?.navigator?.modelContext;
    if (!modelContext?.registerTool) {
      return;
    }
    registerTools(modelContext, propsRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
